import { BrowserModule } from '@angular/platform-browser';
import { NgModule, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { PLATFORM_ID, APP_ID, Inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { RouterModule, Routes } from '@angular/router';
import { CKEditorModule } from '@ckeditor/ckeditor5-angular';
import { NgxCaptchaModule } from 'ngx-captcha';
import { HttpClientModule, HttpClient } from '@angular/common/http';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { DatePipe } from '@angular/common';

import { NgxExtendedPdfViewerModule } from 'ngx-extended-pdf-viewer';


//Components
import { AppComponent } from './app.component';
import { VisualizerComponent } from './pages/visualizer/visualizer.component';
import { VisualizerAdaptativeComponent } from './pages/visualizer/visualizer-adaptative.component';
import { WelcomeComponent } from './pages/welcome/welcome.component';
import { WebTourComponent } from './pages/web.tour/web.tour.component';
import { WebTourInciComponent } from './pages/web.tour.inci/web.tour.inci.component';
import { TestNavComponent } from './pages/test-nav/test-nav.component';
import { LoginComponent } from './pages/login/login.component';
import { FooterComponent } from './pages/footer/footer.component';
import { EndTestComponent } from './pages/end-test/end-test.component';
import { DataSyncComponent } from './pages/data-sync/data-sync.component';
import { UploadComponent } from './pages/upload/upload.component';
import { RecuperarInfoComponent } from './pages/recuperar-info/recuperar-info.component';
import { CambiarContraseniaComponent } from './pages/cambiar-contrasenia/cambiar-contrasenia.component';

import { AccesibilidadComponent } from './components/accesibilidad/accesibilidad.component';

//Services
import { TokenControlService } from './services/state/tokenControl.service';
import { AnswerControlService } from './services/answer/answerControl.service';
import { EncryptionService } from "./services/data/encryption.service";
import { OfflineService } from "./services/data/offline.service";
import { ConfigurationService } from './services/configuration/configuration.service';
import { ConfigurationControlService } from './services/configuration/configurationControl.service';
import { LoggedinGuard } from './guards/loggedin.guard';
import { SyncGuard } from "./guards/sync.guard";
import { NavigateDemoGuard } from "./guards/navigateDemo.guard";
import { NavigateStandardGuard } from "./guards/navigateStandard.guard";

import { SafeHtmlPipe } from './pipes/SafeHtml';
import { WelcomeService } from "./services/welcome/welcome.service";
import { StateService } from "./services/state/state.service";
import { FooterVisualizerComponent } from './pages/footer-visualizer/footer-visualizer.component';
import { ServiceWorkerModule } from '@angular/service-worker';
import { environment } from '../environments/environment';
import { AddComponentService } from './services/addComponent/add-component.service';
import { AdaptativeService } from './services/adaptativeService/adaptative-service';
import { CaptchaService } from "./services/captcha/captcha.service";
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgxSpinnerModule } from 'ngx-spinner';
import { SpinnerComponent } from './pages/spinner/spinner.component';
import { InitAppComponent } from './pages/init-app/init-app.component';
import { LoginDemoComponent } from './pages/login-demo/login-demo.component';
import { ItemsExampleComponent } from './pages/items-example/items-example.component';
import { TermsComponent } from './pages/terms/terms.component'
import { RecaptchaV3Service } from "./services/recaptcha-v3/recaptcha-v3.service";
import { LoginPreIcfesComponent } from './pages/login-pre-icfes/login-pre-icfes.component';
import { WelcomePreIcfesComponent } from './pages/welcome-pre-icfes/welcome-pre-icfes.component';
import { DiscapacidadesComponent } from './pages/discapacidades/discapacidades.component';
import { ReproductorAudioComponent } from './components/reproductor-audio/reproductor-audio.component';
import { ToolKitComponent } from './components/tool-kit/tool-kit.component';
import { VerificacionComponent } from './pages/verificacion/verificacion.component';
import { VideoDisplayerComponent } from './components/custom/videoDisplayer/videoDisplayer.component';
import { SecurityComponent } from './components/security/security.component';
import { CalculadoraComponent } from './components/calculadora/calculadora.component';
import { ValidPCComponent } from './components/valid-pc/valid-pc.component';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { AuthInterceptorService } from './services/interceptor/auth-interceptor.service';
import { LtiLaunchComponent } from './pages/LTI-Integration/lti-launch/lti-launch.component';
import { LaunchStudentComponent } from './pages/LTI-Integration/launchStudent/launchStudent.component';
import { LaunchSupervisorComponent } from './pages/LTI-Integration/launchSupervisor/launchSupervisor.component';
import { LoadingMaskComponent } from './components/loadingMask/loadingMask.component';
import { ExamEvaluationConfComponent } from './components/exam-evaluation-conf/exam-evaluation-conf.component';
import { InternetStatusComponent } from './pages/internet-status/internet-status.component';
import { NetworkConectionService } from './services/network/networkConection.service';
// import { OpenAnswerEditorComponent } from './pages/visualizer/open-answer-editor.component';


export function createTranslateLoader(http: HttpClient) {
  return new TranslateHttpLoader(http, './i18n/', '.json');
}

const appRoutes: Routes = [
  { path: '', component: InitAppComponent },
  { path: 'login', component: LoginComponent, canActivate: [NavigateDemoGuard] },
  { path: 'demo', component: LoginDemoComponent, canActivate: [NavigateDemoGuard] },
  { path: 'preIcfes', component: LoginPreIcfesComponent, canActivate: [NavigateDemoGuard] },
  { path: 'viewtest', component: VisualizerComponent, canActivate: [LoggedinGuard] },
  { path: 'viewtestAdp', component: VisualizerAdaptativeComponent, canActivate: [LoggedinGuard] },
  { path: 'endTest', component: EndTestComponent, canActivate: [LoggedinGuard] },
  { path: 'welcome', component: WelcomeComponent, canActivate: [LoggedinGuard] },
  { path: 'verification', component: VerificacionComponent, canActivate: [LoggedinGuard] },
  { path: 'welcomePreIcfes', component: WelcomePreIcfesComponent },
  { path: 'sync', component: DataSyncComponent, canActivate: [SyncGuard] },
  { path: 'webTourOne', component: WebTourComponent, canActivate: [LoggedinGuard] },
  { path: 'webTourInci', component: WebTourInciComponent, canActivate: [LoggedinGuard] },
  { path: 'uploadFiles', component: UploadComponent, canActivate: [LoggedinGuard] },
  { path: 'recuperarInformacion/:tInfo', component: RecuperarInfoComponent },
  { path: 'cambiarContrasenia', component: CambiarContraseniaComponent },
  { path: 'simulate', component: ItemsExampleComponent, canActivate: [LoggedinGuard] },
  { path: 'terms', component: TermsComponent, canActivate: [LoggedinGuard] },
  { path: 'lti-start', component: LaunchSupervisorComponent, canActivate: [LoggedinGuard] }
];

@NgModule({
  declarations: [
    SafeHtmlPipe,
    AppComponent,
    VisualizerComponent,
    VisualizerAdaptativeComponent,
    WelcomeComponent,
    WebTourComponent,
    WebTourInciComponent,
    TestNavComponent,
    LoginComponent,
    FooterComponent,
    VerificacionComponent,
    EndTestComponent,
    DataSyncComponent,
    UploadComponent,
    RecuperarInfoComponent,
    CambiarContraseniaComponent,
    FooterVisualizerComponent,
    SpinnerComponent,
    InitAppComponent,
    LoginDemoComponent,
    ItemsExampleComponent,
    TermsComponent,
    LoginPreIcfesComponent,
    WelcomePreIcfesComponent,
    DiscapacidadesComponent,
    ReproductorAudioComponent,
    AccesibilidadComponent,
    ToolKitComponent,
    VideoDisplayerComponent,
    SecurityComponent,
    CalculadoraComponent,
    ValidPCComponent,
    LtiLaunchComponent,
    LaunchStudentComponent,
    LaunchSupervisorComponent,
    LoadingMaskComponent,
    ExamEvaluationConfComponent,
    InternetStatusComponent,
    // OpenAnswerEditorComponent,
  ],
  imports: [
    HttpClientModule,
    BrowserModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule.forRoot(),
    RouterModule.forRoot(appRoutes),
    /*LocalStorageModule.withConfig({
      prefix: 'px',
      storageType: 'localStorage'
    }),*/
    /*NgxCaptchaModule.forRoot({
      reCaptcha2SiteKey: '6LcL5lsUAAAAAAqfEC_BYwyT1zMe9OZuxI-WfOW', // optional, can be overridden with 'siteKey' component property
      invisibleCaptchaSiteKey: '6LcL5lsUAAAAAAqfEC_BYwyT1zMe9OZuxI-WfOWy' // optional, can be overridden with 'siteKey' component property
    }),*/
    FormsModule,
    CKEditorModule,
    BrowserModule.withServerTransition({ appId: 'newPlexi' }),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: createTranslateLoader,
        deps: [HttpClient]
      }
    }),
    ServiceWorkerModule.register('./../ngsw-worker.js', { enabled: environment.production }),
    BrowserAnimationsModule,
    NgxSpinnerModule.forRoot({ type: 'ball-scale-multiple' }),
    NgxCaptchaModule,
    NgxExtendedPdfViewerModule
  ],
  providers: [AnswerControlService,
    TokenControlService,
    AddComponentService,
    AdaptativeService,
    EncryptionService,
    LoggedinGuard,
    SyncGuard,
    NavigateStandardGuard,
    NavigateDemoGuard,
    WelcomeService,
    StateService,
    ConfigurationControlService,
    DatePipe,
    ConfigurationService,
    CaptchaService,
    RecaptchaV3Service,
    OfflineService,
    NetworkConectionService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptorService,
      multi: true
    }
    /*{
      provide: RecaptchaLoaderService,
      useValue: new RecaptchaLoaderService("es"),
    }*/],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  bootstrap: [AppComponent]
})
export class AppModule {
  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    @Inject(APP_ID) private appId: string
  ) {
    const platform = isPlatformBrowser(this.platformId) ? 'browser' : 'server';
    //console.log("I'm on the ", platform);
  }
}
